import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Toggle } from '@/ui-kit/components/form/Toggle/Toggle';
import { Badge } from '@/ui-kit/components/Badge/Badge';
import { Grabber } from '@/ui-kit/components/Grabber/Grabber';
import { Checkbox } from '@canvas-logic/clp-shared-ui';
import styles from './StyleGuideOther.module.css';

/**
 * Style guide section for miscellaneous UI components: switches, grabbers,
 * badges, and checkboxes. Mirrors Figma "Canvas Universal Configurator"
 * components/other panel (node-id=4:5798).
 */

export const StyleGuideOther: React.FC = observer(function StyleGuideOther() {
  const [switchIdle, setSwitchIdle] = useState(false);
  const [switchActive, setSwitchActive] = useState(true);

  const [cbVertical, setCbVertical] = useState({
    idle: false,
    hover: false,
    active: true,
    activeHover: true,
  });

  const [cbHorizontal, setCbHorizontal] = useState({
    idle: false,
    hover: false,
    checked: true,
    activeHover: true,
  });

  return (
    <div className={styles.section}>
      {/* ── Switch ── */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Switch</h2>
      </div>
      <div className={styles.demoBlock}>
        <div className={styles.demo}>
          <div className={styles.stateGrid}>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>idle</span>
              <Toggle checked={switchIdle} onChange={() => setSwitchIdle((v) => !v)} />
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>hover</span>
              <div className={styles.forceHover}>
                <Toggle checked={false} readOnly />
              </div>
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>active</span>
              <Toggle checked={switchActive} onChange={() => setSwitchActive((v) => !v)} />
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>active.hover</span>
              <div className={styles.forceHover}>
                <Toggle checked={true} readOnly />
              </div>
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>disabled</span>
              <Toggle checked={true} disabled />
            </div>
          </div>
          <div className={styles.stateGrid}>
            <div className={styles.stateItem}>
              <div className={styles.forceFocus}>
                <Toggle checked={false} readOnly />
              </div>
            </div>
            <div className={styles.stateItem}>
              <div className={styles.forceFocus}>
                <Toggle checked={true} readOnly />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Grabber ── */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Grabber</h2>
      </div>
      <div className={styles.demoBlock}>
        <div className={styles.demo}>
          <div className={styles.grabberDemo}>
            <Grabber />
          </div>
        </div>
      </div>

      {/* ── Badge ── */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Badge</h2>
      </div>
      <div className={styles.demoBlock}>
        <div className={styles.demo}>
          <div className={styles.badgeTable}>
            <div className={styles.badgeRow}>
              <Badge variant="good">Information</Badge>
              <Badge variant="good" showIcon={false}>
                Information
              </Badge>
              <Badge variant="good" />
            </div>
            <div className={styles.badgeRow}>
              <Badge variant="bad">Information</Badge>
              <Badge variant="bad" showIcon={false}>
                Information
              </Badge>
              <Badge variant="bad" />
            </div>
            <div className={styles.badgeRow}>
              <Badge variant="info">Information</Badge>
              <Badge variant="info" showIcon={false}>
                Information
              </Badge>
              <Badge variant="info" />
            </div>
            <div className={styles.badgeRow}>
              <Badge variant="neutral">Information</Badge>
              <Badge variant="neutral" showIcon={false}>
                Information
              </Badge>
              <Badge variant="neutral" />
            </div>
          </div>
        </div>
      </div>

      {/* ── Checkbox.vertical-card ── */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Checkbox.vertical-card</h2>
      </div>
      <div className={styles.demoBlock}>
        <div className={styles.demo}>
          <div className={styles.stateGrid}>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>idle</span>
              <Checkbox
                checked={cbVertical.idle}
                onChange={() => setCbVertical((s) => ({ ...s, idle: !s.idle }))}
                noErrorPlaceholder
              />
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>hover</span>
              <div className={styles.forceHover}>
                <Checkbox
                  checked={cbVertical.hover}
                  onChange={() => setCbVertical((s) => ({ ...s, hover: !s.hover }))}
                  noErrorPlaceholder
                />
              </div>
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>active</span>
              <Checkbox
                checked={cbVertical.active}
                onChange={() => setCbVertical((s) => ({ ...s, active: !s.active }))}
                noErrorPlaceholder
              />
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>active.hover</span>
              <div className={styles.forceHover}>
                <Checkbox
                  checked={cbVertical.activeHover}
                  onChange={() => setCbVertical((s) => ({ ...s, activeHover: !s.activeHover }))}
                  noErrorPlaceholder
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Checkbox.horizontal-card ── */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Checkbox.horizontal-card</h2>
      </div>
      <div className={styles.demoBlock}>
        <div className={styles.demo}>
          <div className={styles.stateGrid}>
            <div className={styles.stateItemReverse}>
              <Checkbox
                checked={cbHorizontal.idle}
                onChange={() => setCbHorizontal((s) => ({ ...s, idle: !s.idle }))}
                noErrorPlaceholder
              />
              <span className={styles.stateLabel}>idle</span>
            </div>
            <div className={styles.stateItemReverse}>
              <div className={styles.forceHover}>
                <Checkbox
                  checked={cbHorizontal.hover}
                  onChange={() => setCbHorizontal((s) => ({ ...s, hover: !s.hover }))}
                  noErrorPlaceholder
                />
              </div>
              <span className={styles.stateLabel}>hover</span>
            </div>
            <div className={styles.stateItemReverse}>
              <Checkbox
                checked={cbHorizontal.checked}
                onChange={() => setCbHorizontal((s) => ({ ...s, checked: !s.checked }))}
                noErrorPlaceholder
              />
              <span className={styles.stateLabel}>checked</span>
            </div>
            <div className={styles.stateItemReverse}>
              <div className={styles.forceHover}>
                <Checkbox
                  checked={cbHorizontal.activeHover}
                  onChange={() => setCbHorizontal((s) => ({ ...s, activeHover: !s.activeHover }))}
                  noErrorPlaceholder
                />
              </div>
              <span className={styles.stateLabel}>active.hover</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Extras: Design Tokens ── */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Design Tokens</h2>
      </div>
      <div className={styles.demoBlock}>
        <div className={styles.demo}>
          <table className={styles.tokenTable}>
            <thead>
              <tr>
                <th>Component</th>
                <th>Token</th>
                <th>CSS Variable</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Switch (off)</td>
                <td>Track</td>
                <td>
                  <code>--color-grey-300</code>
                </td>
                <td>#d9d9d9</td>
              </tr>
              <tr>
                <td>Switch (on)</td>
                <td>Track</td>
                <td>
                  <code>--color-toggle</code>
                </td>
                <td>linear-gradient brand-500</td>
              </tr>
              <tr>
                <td>Switch (disabled)</td>
                <td>Track</td>
                <td>
                  <code>--color-toggle-disabled</code>
                </td>
                <td>brand-500-24</td>
              </tr>
              <tr>
                <td>Switch</td>
                <td>Knob shadow</td>
                <td>
                  <code>--shadow-switch</code>
                </td>
                <td>0 4px 8px rgba(0,0,0,0.12)</td>
              </tr>
              <tr>
                <td>Badge (good)</td>
                <td>Background</td>
                <td>
                  <code>--color-status-good</code>
                </td>
                <td>#4aff53</td>
              </tr>
              <tr>
                <td>Badge (bad)</td>
                <td>Background</td>
                <td>
                  <code>--color-status-bad</code>
                </td>
                <td>#f9586d</td>
              </tr>
              <tr>
                <td>Badge (info)</td>
                <td>Background</td>
                <td>
                  <code>--color-status-info</code>
                </td>
                <td>#ffe23f</td>
              </tr>
              <tr>
                <td>Badge (neutral)</td>
                <td>Background</td>
                <td>
                  <code>--color-grey-300</code>
                </td>
                <td>#d9d9d9</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
});
